app.component("patientClaimableItems", {
	templateUrl: "app/src/js/components/patientClaimableItems/patientClaimableItems.html",
	controller: "patientClaimableItemsController",
	bindings: {
		patientId: "<",
	},
});

app.controller("patientClaimableItemsController", [
	"$scope",
	"$q",
	"CurrentPatient",
	"TyroAPI",
	"TyroClaimableItems",
	"countryConfig",
	"LiveReloadService",
	"PUSH_EVENTS",
	function ($scope, $q, CurrentPatient, TyroAPI, TyroClaimableItems, countryConfig, LiveReloadService, PUSH_EVENTS) {

		var $ctrl = this;

		$ctrl.enabled = false;
		$ctrl.items = [];
		$ctrl.claimableItems = [];
		$ctrl.selectedItem = null;
		$ctrl.fee = null;
		// Two comments, because the form offers two ways to record one: itemComment rides on a picked item,
		// comment stands on its own for work nobody has coded yet.
		$ctrl.itemComment = '';
		$ctrl.comment = '';
		$ctrl.showPracticeItems = false;
		$ctrl.practiceItems = [];
		$ctrl.newItem = blankNewItem();

		$ctrl.isLoading = false;
		$ctrl.isSaving = false;
		$ctrl.loadError = null;
		$ctrl.saveError = null;

		$ctrl.$onInit = function () {
			$ctrl.enabled = !!countryConfig.medicareFeatureEnabled;
			if (!$ctrl.enabled || !patientId()) {
				return;
			}

			$ctrl.isLoading = true;
			$ctrl.loadError = null;

			// Independent loads, so the picker is usable as soon as the reference list lands.
			TyroClaimableItems.load().then(function (items) {
				setClaimableItems(items);
			}).catch(function (error) {
				$ctrl.loadError = errorMessage(error, "Unable to load the claimable item list. Please try again.");
			});

			loadItems().catch(function (error) {
				$ctrl.loadError = errorMessage(error, "Unable to load this patient's claimable items. Please try again.");
			}).finally(function () {
				$ctrl.isLoading = false;
			});

			// A claim lodged, released or deleted - from the claims screen, another user's, or a funder's
			// webhook refusing one - takes recorded items off this list or puts them back. Reloaded rather than
			// patched: which items a claim carries is the server's reconciliation, not something the event says.
			LiveReloadService.subscribe($scope, {
				events: [PUSH_EVENTS.CLAIM_ADDED, PUSH_EVENTS.CLAIM_UPDATED, PUSH_EVENTS.CLAIM_DELETED],
				isRelevant: function (payload) {
					return LiveReloadService.sameId(payload.patientId, patientId());
				},
				reload: function () {
					// Not under a save of this list's own, which sets the rows itself when it lands.
					if ($ctrl.isSaving) {
						return;
					}
					// Quietly: the list on screen is only as stale as it was.
					loadItems().catch(function () {});
				},
			});
		};

		function loadItems() {
			return TyroAPI.getPatientClaimableItems(patientId(), 'PENDING').then(function (response) {
				$ctrl.items = sortByDateAdded(response.data || []);
			});
		}

		// --- The practice's own item codes ---

		$ctrl.togglePracticeItems = function () {
			$ctrl.showPracticeItems = !$ctrl.showPracticeItems;
			if (!$ctrl.showPracticeItems) {
				$ctrl.newItem = blankNewItem();
				$ctrl.saveError = null;
			}
		};

		function blankNewItem() {
			return { item_code: '', name: '', description: '', fee: null };
		}


		function setClaimableItems(items) {
			$ctrl.claimableItems = items;
			$ctrl.practiceItems = items.filter(TyroClaimableItems.isPracticeItem);
		}

		$ctrl.isNewItemValid = function () {
			const item = $ctrl.newItem;
			return !!(item.item_code || '').trim() && !$ctrl.newItemCodeIssue() && !!(item.name || '').trim() &&
				item.fee !== null && item.fee !== undefined && item.fee !== '' && item.fee >= 0;
		};

		/**
		 * Why this item code would be refused, or null.
		 *
		 * Medicare takes an item number as a number: "Invalid value of D42701 supplied for Item Number. The value
		 * supplied must be numeric (1-99999) and must not be set to or equal zero. Spaces are not allowed." It
		 * says so at lodgement, days after the code was saved and against a claim that has already been built on
		 * it - so it is said here instead, where the code is typed once and fixed in a second.
		 *
		 * A code with a letter in it is usually a prosthesis or a miscellaneous code rather than an MBS item.
		 * Those are claimed under a different service code type, which CatTrax does not send - every line goes as
		 * type C, an MBS item - so such a code cannot be claimed here at all yet, and saying "numeric" is the
		 * honest answer rather than implying a format fix would do it.
		 */
		$ctrl.newItemCodeIssue = function () {
			const code = ($ctrl.newItem.item_code || '').trim();
			if (!code) {
				return null;
			}
			if (/\s/.test($ctrl.newItem.item_code || '')) {
				return "An item code cannot contain spaces.";
			}
			if (!/^\d{1,5}$/.test(code)) {
				return "Medicare only accepts a numeric MBS item number of 1 to 5 digits. A code with letters in "
					+ "it - a prosthesis or miscellaneous code - cannot be claimed through CatTrax yet.";
			}
			if (Number(code) === 0) {
				return "An item code cannot be zero.";
			}
			return null;
		};

		$ctrl.addPracticeItem = function () {
			if (!$ctrl.isNewItemValid() || $ctrl.isSaving) {
				return;
			}

			$ctrl.isSaving = true;
			$ctrl.saveError = null;

			const request = {
				item_code: $ctrl.newItem.item_code.trim(),
				name: $ctrl.newItem.name.trim(),
				description: ($ctrl.newItem.description || '').trim() || null,
				available: true,
				fee: $ctrl.newItem.fee,
			};

			TyroAPI.createClaimableItem(request).then(function (response) {
				return refreshClaimableItems().then(function () {
					$ctrl.selectedItem = $ctrl.claimableItems.find(
						option => option.tyro_claimable_item_id === response.data.tyro_claimable_item_id) || response.data;
					$ctrl.onItemSelected();
					$ctrl.newItem = blankNewItem();
				});
			}).catch(function (error) {
				$ctrl.saveError = errorMessage(error, "Unable to add the item code. Please try again.");
			}).finally(function () {
				$ctrl.isSaving = false;
			});
		};

		// Loads a code into the form beside the list, the way the prompt manager does. Saving a code the
		// practice already holds overwrites it, so editing one is the same action as adding it.
		$ctrl.editPracticeItem = function (item) {
			$ctrl.newItem = {
				item_code: item.item_code || '',
				name: item.name || '',
				description: item.description || '',
				fee: (item.fee === undefined || item.fee === null) ? null : item.fee,
			};
			$ctrl.saveError = null;
		};

		$ctrl.cancelPracticeItemEdit = function () {
			$ctrl.newItem = blankNewItem();
			$ctrl.saveError = null;
		};

		$ctrl.isPracticeItemFormDirty = function () {
			const item = $ctrl.newItem;
			return !!(item.item_code || item.name || item.description
				|| (item.fee !== null && item.fee !== undefined && item.fee !== ''));
		};

		// Removes the code from what the practice can pick from.
		$ctrl.deletePracticeItem = function (item) {
			if ($ctrl.isSaving) {
				return;
			}

			$ctrl.isSaving = true;
			$ctrl.saveError = null;

			TyroAPI.deleteClaimableItem(item.tyro_claimable_item_id).then(function () {
				// The picker cannot be left holding an item that no longer exists.
				if ($ctrl.selectedItem && $ctrl.selectedItem.tyro_claimable_item_id === item.tyro_claimable_item_id) {
					$ctrl.selectedItem = null;
					$ctrl.fee = null;
				}
				return refreshClaimableItems();
			}).catch(function (error) {
				$ctrl.saveError = errorMessage(error, "Unable to delete the item code. Please try again.");
			}).finally(function () {
				$ctrl.isSaving = false;
			});
		};

		$ctrl.searchClaimableItems = function (search) {
			return TyroClaimableItems.search($ctrl.claimableItems, search);
		};

		$ctrl.claimableItemLabel = function (item) {
			return TyroClaimableItems.label(item);
		};

		// Shows the fee held against the practice's copy, so changing it reads plainly as an overwrite. A
		// SYSTEM item has no fee and leaves the field empty.
		$ctrl.onItemSelected = function () {
			$ctrl.fee = feeOrNull($ctrl.selectedItem);
		};

		$ctrl.isFeeValid = function () {
			return isFeeBlank() || $ctrl.fee >= 0;
		};

		// The item half: a code, what it was for, and what the practice charges.
		$ctrl.canAddItem = function () {
			return !!$ctrl.selectedItem && !$ctrl.isSaving && $ctrl.isFeeValid();
		};

		$ctrl.addItem = function () {
			if (!$ctrl.canAddItem()) {
				return;
			}

			record(savePracticeFee($ctrl.selectedItem).then(function (item) {
				return { id: item.tyro_claimable_item_id, comment: trimmed($ctrl.itemComment) };
			}), function () {
				$ctrl.selectedItem = null;
				$ctrl.fee = null;
				$ctrl.itemComment = '';
			});
		};

		// The comment half: a clinician saying what they did without picking what it bills as - "initial
		// consult" - for an administrator to code at claim time.
		$ctrl.canAddComment = function () {
			return !!trimmed($ctrl.comment) && !$ctrl.isSaving;
		};

		$ctrl.addComment = function () {
			if (!$ctrl.canAddComment()) {
				return;
			}

			record($q.resolve({ id: null, comment: trimmed($ctrl.comment) }), function () {
				$ctrl.comment = '';
			});
		};

		// Both halves write the same kind of row, so they share the one call, the one error and the one
		// spinner. Only the half that was used is cleared - the other may be half-typed.
		function record(recording, reset) {
			$ctrl.isSaving = true;
			$ctrl.saveError = null;

			recording.then(function (toRecord) {
				return TyroAPI.addPatientClaimableItem(toRecord.id, patientId(), toRecord.comment)
					.then(function (response) {
						reset();
						return recordAdded(response.data);
					});
			}).catch(function (error) {
				$ctrl.saveError = errorMessage(error, "Unable to add the claimable item. Please try again.");
			}).finally(function () {
				$ctrl.isSaving = false;
			});
		}

		function trimmed(value) {
			return (value || '').trim() || null;
		}

		// The fee is what the practice charges, not something particular to this patient, so it overwrites the
		// fee on the practice's own copy - the next person to pick the item sees the new price. Pricing a
		// SYSTEM item creates the practice's copy of that code. Resolves with the item to record.
		function savePracticeFee(selected) {
			if (isFeeBlank() || $ctrl.fee === feeOrNull(selected)) {
				return $q.resolve(selected);
			}

			const request = {
				item_code: selected.item_code,
				name: selected.name,
				description: selected.description,
				funder: selected.funder,
				available: selected.available !== false,
				fee: $ctrl.fee,
				search_term: selected.search_term,
				other_search_terms: selected.other_search_terms,
			};

			const saved = TyroClaimableItems.isPracticeItem(selected)
				? TyroAPI.updateClaimableItem(selected.tyro_claimable_item_id, request)
				: TyroAPI.createClaimableItem(request);

			return saved.then(function (response) {
				return refreshClaimableItems().then(function () {
					$ctrl.selectedItem = $ctrl.claimableItems.find(
						option => option.tyro_claimable_item_id === response.data.tyro_claimable_item_id) || response.data;
					return $ctrl.selectedItem;
				});
			});
		}

		// Saving changes what the drop-down should offer - a new practice copy supersedes the SYSTEM item it
		// duplicates - so the shared list is refetched rather than patched.
		function refreshClaimableItems() {
			return TyroClaimableItems.load(true).then(function (items) {
				setClaimableItems(items);
			}).catch(function () {
				// The item saved either way, and a stale drop-down is not worth failing the add over.
			});
		}

		// Drops whatever the new record supersedes: the same item re-added (the backend keys on patient and
		// item, so it refreshed in place), and any copy of the same code under a different id - pricing a
		// SYSTEM item records the practice's copy instead, and the patient must not carry the code twice.
		//
		// Only a coded record supersedes anything. Two comments are two separate pieces of work with no code
		// to tell them apart, so a second one joins the first rather than replacing it.
		function recordAdded(record) {
			const superseded = record.item_code
				? $ctrl.items.filter(item => item.item_code === record.item_code
					&& item.tyro_claimable_item_id !== record.tyro_claimable_item_id)
				: [];
			const replacedIds = superseded.map(item => item.tyro_claimable_item_id)
				.concat(record.tyro_claimable_item_id);

			$ctrl.items = sortByDateAdded(
				$ctrl.items.filter(item => replacedIds.indexOf(item.tyro_claimable_item_id) === -1).concat(record));

			return $q.all(superseded.map(item =>
				TyroAPI.removePatientClaimableItem(item.tyro_claimable_item_id, patientId())));
		}

		function isFeeBlank() {
			return $ctrl.fee === null || $ctrl.fee === undefined || $ctrl.fee === '';
		}

		function feeOrNull(item) {
			return (item && item.fee !== undefined && item.fee !== null) ? item.fee : null;
		}

		$ctrl.removeItem = function (item) {
			if ($ctrl.isSaving) {
				return;
			}

			$ctrl.isSaving = true;
			$ctrl.saveError = null;

			TyroAPI.removePatientClaimableItem(item.tyro_claimable_item_id, patientId()).then(function () {
				$ctrl.items = $ctrl.items.filter(recorded => recorded.tyro_claimable_item_id !== item.tyro_claimable_item_id);
			}).catch(function (error) {
				$ctrl.saveError = errorMessage(error, "Unable to remove the claimable item. Please try again.");
			}).finally(function () {
				$ctrl.isSaving = false;
			});
		};

		// Only the practice's own items carry a fee, so this is what the practice expects to bill, not what
		// any funder will pay - the benefit is only known once the claim is assessed.
		$ctrl.itemsTotal = function () {
			return $ctrl.items.reduce((total, item) => total + (item.fee || 0), 0);
		};

		function patientId() {
			return $ctrl.patientId || (CurrentPatient.patient && CurrentPatient.patient.id);
		}

		// Newest last, so an item just added appears at the bottom, where the picker left it.
		function sortByDateAdded(items) {
			return items.slice().sort((a, b) => new Date(a.date_created) - new Date(b.date_created));
		}

		function errorMessage(error, fallback) {
			return (error && error.data && error.data.message) || fallback;
		}
	},
]);
